// React and related libraries
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useHistory, Link, useParams } from "react-router-dom";
import moment from "moment";

// State management
import store from "store";

// Material UI components
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";

// Custom components and hooks
import useFetch from "../../hooks/useFetch";
import { useSnackbar } from "../../context/snackbar";
import BoxCountdown from "../../components/CountDownTimer";
import StyledBox from "../../components/StyledBox";
import Guidelines from "./Guidelines";
import RegFooter from "./RegFooter";

// Theme and assets
import theme from "../../assets/v3/theme";
import colors from "../../assets/v3/base/colors";
import LogoURLMap, { hostnamesMap } from "../../components/LogoURLMap";
import HeroCarousel from "./components/HeroCarousel";
import {
  CampusSelectionStage,
  SimpleDetailsStage,
  ConfirmationStage,
  OtpVerificationStage,
} from "./components/stages";

// API services
import {
  createOtpRegister,
  newRegister,
  getRegistrationFormConfig,
} from "../../apis/UserService";

export const useStyles = makeStyles((theme) => ({
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
    textAlign: "left",
  },
  label: {
    lineHeight: "1.5",
    // marginBottom: theme.spacing(0.5),
  },
  inputWithIcon: {
    border: "none",
  },
  titleContainer: {
    backgroundColor: colors.background.default,
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      background: `linear-gradient(135deg, ${
        colors.gradients.tirumala.main
      } 0%, ${colors.gradients.tirumala.state} 100%)`,
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background:
          "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)",
        zIndex: 0,
      },
    },
  },
  heroContent: {
    position: "relative",
    zIndex: 1,
    padding: theme.spacing(6, 4),
    textAlign: "center",
    width: "100%",
    maxWidth: "900px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  heroLogo: {
    maxHeight: "150px",
    maxWidth: "400px",
    marginBottom: theme.spacing(5),
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
    [theme.breakpoints.down("sm")]: {
      maxHeight: "80px",
      marginBottom: theme.spacing(2),
      filter: "brightness(0) invert(1)",
    },
  },
  heroTitle: {
    fontSize: "3.5rem",
    fontWeight: 700,
    color: colors.dark.main,
    marginBottom: theme.spacing(3),
    lineHeight: 1.2,
    letterSpacing: "-0.5px",
    [theme.breakpoints.down("md")]: {
      fontSize: "3rem",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "2rem",
      color: colors.white.main,
      textShadow: "2px 2px 8px rgba(0,0,0,0.3)",
    },
  },
  heroSubtitle: {
    fontSize: "1.5rem",
    fontWeight: 300,
    color: colors.grey[700],
    marginBottom: theme.spacing(5),
    opacity: 0.95,
    lineHeight: 1.8,
    maxWidth: "600px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.1rem",
      marginBottom: theme.spacing(2),
      color: colors.white.main,
      textShadow: "1px 1px 4px rgba(0,0,0,0.2)",
    },
  },
  heroFeatures: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(3),
    marginTop: theme.spacing(4),
    width: "100%",
    maxWidth: "800px",
    justifyContent: "center",
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "center",
    },
  },
  heroFeature: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    color: colors.grey[700],
    "& svg": {
      fontSize: "1.5rem",
      color: colors.gradients.tirumala.main,
    },
    [theme.breakpoints.down("sm")]: {
      color: colors.white.main,
      opacity: 0.9,
      "& svg": {
        opacity: 0.8,
        color: colors.white.main,
      },
    },
  },
  startButton: {
    padding: theme.spacing(1.5, 5),
    fontSize: "1.1rem",
    fontWeight: 600,
    borderRadius: theme.spacing(3),
    textTransform: "none",
    boxShadow: theme.shadows[4],
    transition: "all 0.3s ease",
    background: colors.gradients.tirumala.main,
    color: colors.white.main,
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[8],
      background: colors.gradients.tirumala.main,
    },
  },
  rightColumnContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    maxWidth: "420px",
    padding: theme.spacing(2),
    gap: theme.spacing(1.5),
  },
  rightColumnTitle: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: colors.dark.main,
    marginBottom: theme.spacing(0.5),
  },
  rightColumnSubtitle: {
    fontSize: "0.95rem",
    color: colors.grey[600],
  },
  rightColumnFeatures: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    width: "100%",
  },
  rightColumnFeature: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    textAlign: "left",
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    backgroundColor: colors.grey[50],
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: colors.grey[100],
      transform: "translateX(4px)",
    },
    "& svg": {
      color: colors.gradients.tirumala.main,
      fontSize: "1.5rem",
    },
  },
  heading: {
    textAlign: "center",
    lineHeight: 1.5,
    fontWeight: "bolder",
    background: `-webkit-linear-gradient(#ED1B24, #074EA0)`,
    "-webkit-background-clip": "text",
    "-webkit-text-fill-color": "transparent",
  },
  back: {
    paddingLeft: 0,
    color: colors.dark.main,
  },
  option: {
    fontWeight: "400",
    textDecoration: "none",
    textTransform: "none",
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
  },
  helperText: {
    marginLeft: theme.spacing(1),
  },
  blueGradientBtn: {
    background: `linear-gradient(310deg, ${colors.gradients.tirumala.main}, ${
      colors.gradients.tirumala.state
    })`,
    color: "white",
  },
  blueGradientBtn2: {
    border: "1px solid transparent",
    background: `
      linear-gradient(to right, white, white), 
      linear-gradient(to right, ${colors.gradients.tirumala.state}, ${
      colors.gradients.tirumala.main
    })
    `,
    backgroundClip: "padding-box, border-box",
    backgroundOrigin: "padding-box, border-box",
    color: colors.gradients.tirumala.state,
  },
  pageTitleContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.info.dark,
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
  },
  pageTitleSubContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    maxWidth: 900,
    flexGrow: 1,
  },
  pageTitle: {
    color: colors.white.main,
    flexGrow: 1,
  },
  nextText: {
    color: colors.white.main,
  },
  formContainer: {
    backgroundColor: colors.background.default,
    [theme.breakpoints.up("md")]: {
      boxShadow: theme.shadows[3],
      borderRadius: theme.spacing(2),
      paddingTop: theme.spacing(2),
      marginTop: theme.spacing(10),
    },
  },
  relative: {
    position: "relative",
  },
  cardTitle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  cardTitleText: {
    color: colors.info.dark,
    flexGrow: 1,
    fontWeight: "bold",
  },
  cardSubTitle: {
    color: colors.info.dark,
  },
  logoScrollContainer: {
    position: "relative",
    width: 600,
    height: 785,
    overflow: "hidden",
  },

  logoScrollInner: {
    display: "flex",
    alignItems: "center",
    width: "auto",
    height: "100%",
    gap: -8,
    animation: "$scrollLogos 8s linear infinite alternate",
  },
  chipContainer: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  chip: {
    flex: 1,
    minHeight: 36,
    fontSize: "0.875rem",
    fontWeight: 500,
    borderRadius: theme.spacing(1),
    border: `2px solid ${colors.grey[300]}`,
    backgroundColor: colors.white.main,
    color: colors.grey[600],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: colors.grey[50],
    },
    "&.selected": {
      backgroundColor: colors.white.main,
      color: colors.grey[800],
      border: `2px solid #4CAF50`,
      "& .check-icon": {
        color: "#4CAF50",
      },
    },
  },
}));

export const PageTitle = ({ title, next }) => {
  const classes = useStyles();
  return (
    <Box className={classes.pageTitleContainer}>
      <Box className={classes.pageTitleSubContainer}>
        <Typography variant="h5" className={classes.pageTitle}>
          {title}
        </Typography>
        {next && (
          <Typography
            variant="caption"
            className={classes.nextText}
            component="p"
          >
            next: {next}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const CardPageTitle = ({ title, next }) => {
  const classes = useStyles();
  return (
    <Box className={classes.cardTitle} flexGrow={1}>
      <Box className={classes.pageTitleSubContainer}>
        <Typography variant="h5" className={classes.cardTitleText}>
          {title}
        </Typography>
        {next && (
          <Typography
            variant="caption"
            className={classes.cardSubTitle}
            component="p"
          >
            next: {next}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const steps = {
  CAMPUS_SELECTION: "CAMPUS_SELECTION",
  SIMPLE_DETAILS: "SIMPLE_DETAILS",
  CONFIRMATION: "CONFIRMATION",
  OTP_VERIFICATION: "OTP_VERIFICATION",
};

const stepLabels = [
  "Campus Selection",
  "Details",
  "Confirmation",
  "OTP Verification",
];

const NewRegistration = () => {
  // Auto-select grade if only one option
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [l1, e1, d1, f1, r1] = useFetch(createOtpRegister);
  const [l2, e2, d2, f2, r2] = useFetch(newRegister);
  const [l3, e3, d3, f3, r3] = useFetch(getRegistrationFormConfig);
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(90);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [boards, setBoards] = useState(null);
  const [grade, setGrade] = useState(null);
  const [branch, setBranch] = useState(null);
  const [batch, setBatch] = useState(null);
  const [zone, setZone] = useState(null);
  const [schoolOrCollege, setSchoolOrCollege] = useState("");
  const [step, setStep] = useState(steps.CAMPUS_SELECTION);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { openSnackbar } = useSnackbar();
  const history = useHistory();
  const { branch: paramBranch } = useParams();
  const instituteId =
    hostnamesMap[window.location.hostname] || "5d679d49c136660a09596d85";
  const payload = d3 ? d3.payload : {};
  const sysTime = d3 ? d3.sysTime : null;
  const title = payload && payload.title ? payload.title : null;
  const stuff = LogoURLMap[instituteId] || {};
  const brandImagesOfInstitute = Object.entries(stuff)
    .filter(([key]) => key.includes("brand_image"))
    .map(([, value]) => value);
  const brandMaintainedInstitutes = [
    "5e7370083d8520a40c4ceaaa",
    "61e850055876f313e30ea1de",
    "66d7fd5ed38d3afac6db0696",
    "68c8f4482174da0ddf40bd87",
  ];
  const academicYear =
    payload && payload.academicYear ? payload.academicYear : null;
  const branchesList = d3 ? d3.branchesList : [];
  const branchesData = d3 ? d3.branchesData : {};
  const batchesData = d3 ? d3.batchesData : {};
  const zonesList = d3 ? d3.zonesList : [];
  const zonesBranchesMap = d3 ? d3.zonesBranchesMap : {};
  const haveZonesFilter =
    payload && payload.zones_filter ? payload.zones_filter : false;
  const haveSchoolOrCollegeFilter =
    payload && payload.school_college_filter
      ? payload.school_college_filter
      : false;

  const school_classes = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];
  const college_classes = ["11", "12"];

  const myZonesList = useMemo(() => {
    let schoolZonesMap = {};
    let collegeZonesMap = {};

    if (!branchesList || branchesList.length === 0) {
      return {
        school: [],
        college: [],
      };
    }

    for (let i = 0; i < branchesList.length; i++) {
      const branch = branchesList[i];
      const branchClasses = branchesData[branch.branchCode].classes;
      if (branchClasses.some((cls) => school_classes.includes(cls))) {
        schoolZonesMap[branch.zoneId] = {
          zoneName: branch.zoneName,
          zoneId: branch.zoneId,
        };
      }
      if (branchClasses.some((cls) => college_classes.includes(cls))) {
        collegeZonesMap[branch.zoneId] = {
          zoneName: branch.zoneName,
          zoneId: branch.zoneId,
        };
      }
    }
    return {
      school: Object.values(schoolZonesMap),
      college: Object.values(collegeZonesMap),
    };
  }, [branchesList, branchesData]);

  const myBranchesList = useMemo(() => {
    let some = branchesList;
    if (haveSchoolOrCollegeFilter) {
      some = some.filter((branch) => {
        const branchClasses = branchesData[branch.branchCode].classes;
        if (schoolOrCollege === "school") {
          return branchClasses.some((cls) => school_classes.includes(cls));
        } else if (schoolOrCollege === "college") {
          return branchClasses.some((cls) => college_classes.includes(cls));
        }
        return false;
      });
    }

    // Then filter by zone if enabled and zone is selected
    if (haveZonesFilter) {
      console.log("Zone selected:", zone);

      if (zone && zonesBranchesMap[zone.zoneId]) {
        some = zonesBranchesMap[zone.zoneId].filter((branch) => {
          const branchClasses = branchesData[branch.branchCode].classes;
          if (schoolOrCollege === "school") {
            return branchClasses.some((cls) => school_classes.includes(cls));
          } else if (schoolOrCollege === "college") {
            return branchClasses.some((cls) => college_classes.includes(cls));
          }
          return false;
        });
        console.log("Filtered branches for zone:", some);
      } else {
        some = [];
      }
    }

    return some;
  }, [
    haveZonesFilter,
    zone,
    schoolOrCollege,
    branchesList,
    branchesData,
    zonesBranchesMap,
  ]);

  const date = useMemo(() => {
    if (payload) {
      if (
        payload &&
        payload[`${paramBranch}`] &&
        payload[`${paramBranch}`].date
      ) {
        return payload[`${paramBranch}`].date;
      }
      if (payload && payload.date) {
        return payload.date;
      }
      return null;
    }
    return null;
  }, [payload, paramBranch]);

  useEffect(() => {
    if (haveSchoolOrCollegeFilter) {
      setSchoolOrCollege("college");
    }
  }, [haveSchoolOrCollegeFilter]);

  const clear = () => {
    setOtpSent(false);
    r1();
    r2();
  };

  // Clear all selected data and reset to initial state
  const clearAllData = () => {
    // setBranch(null);
    // setZone(null);
    setGrade(null);
    setBatch(null);
    setBoards(null);
    setPhoneNumber("");
    // setSchoolOrCollege("college");
    setStep(steps.CAMPUS_SELECTION);
    setDialogOpen(false);
    clear();
  };

  const countDown = useMemo(() => {
    if (!date || !sysTime) return null;
    const from = new Date(sysTime).getTime();
    const to = new Date(date).getTime();
    const difference = to - from;

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    return {
      days,
      hours,
      minutes,
      seconds,
    };
  }, [date, sysTime]);

  const submitOtp = (values) => {
    const myGrade = `${grade}`.padStart(2, "0");
    const myCourse = (() => {
      if (batch.toLowerCase().includes("jee_main")) {
        return "JEE_MAIN";
      }
      if (batch.toLowerCase().includes("jee_advanced")) {
        return "JEE_ADVANCED";
      }
      if (batch.toLowerCase().includes("neet")) {
        return "NEET";
      }
      if (batch.toLowerCase().includes("jee-main")) {
        return "JEE_MAIN";
      }
      if (batch.toLowerCase().includes("jee-advanced")) {
        return "JEE_ADVANCED";
      }
      return batchesData[batch];
    })();
    const data = {
      phoneNumber,
      branchId: branch,
      batch: batchesData[batch],
      academicYear,
      current_ay: academicYear,
      fullName: values.fullName,
      tt: true,
      otp: values.otp,
      course: myCourse,
      boards,
      grade: myGrade,
      filterKey: `${branch}_${boards}_${myGrade}`,
      class: `${grade}`,
    };
    f2(data);
  };

  useEffect(() => {
    let timer;
    if (otpSent) {
      timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [otpSent]);

  useEffect(() => {
    if (d1) {
      if (d1 && d1.data && d1.data.status === "SUCCESS") {
        openSnackbar("OTP sent successfully", "success");
        setOtpSent(true);
        setStep(steps.OTP_VERIFICATION);
        setResendTimer(90);
        setIsResendDisabled(true);
      } else if (
        d1.data &&
        d1.data.status === "FAILURE" &&
        d1.data.message === "The phone number already exists in our records."
      ) {
        openSnackbar(d1.data.message, "warning", false, {
          customActions: [
            {
              text: "Login",
              onClick: () => history.push("/reg-login"),
              className: "success",
              closeOnClick: true,
            },
          ],
          showCloseIcon: true,
          actionsPosition: "center",
          onClose: () => history.push("/reg-login"),
          autoClose: true,
          triggerType: "none",
        });
      } else if (d1.data && d1.data.status === "FAILURE") {
        openSnackbar(d1.data.message, "warning", false);
      }
    }

    if (e1) {
      openSnackbar("Failed to send OTP", "error");
    }

    return () => {
      r1();
    };
  }, [d1, e1, openSnackbar]);

  const resendOtp = () => {
    // logic to resend OTP
    f1({
      phoneNumber,
      branchName: branchesData[branch] ? branchesData[branch].branchName : "",
      branch,
      academicYear,
    });
    setOtpSent(true);
    setResendTimer(90);
    setIsResendDisabled(true);
  };

  const imageKey = useMemo(() => {
    // if(paramBranch) {
    //   return `url_${paramBranch}`;
    // }
    return "url";
  }, [paramBranch, branch]);

  useEffect(() => {
    if (d2) {
      if (d2.data.status === "FAILURE") {
        openSnackbar(d2.data.message, "error");
      } else {
        openSnackbar("Registration successful", "success");
        localStorage.setItem("isAuthenticated", true);
        store.set("user", d2.data);
        history.push(`/student-details`);
      }
    }
    if (e2) {
      openSnackbar(e2, "error");
    }

    return () => {
      r2();
    };
  }, [d2, e2, openSnackbar]);

  useEffect(() => {
    if (d3) {
      if (d3.data && d3.data.status === "FAILURE") {
        openSnackbar(d3.data.message, "error");
      }
    }

    if (e3) {
      openSnackbar("Failed to fetch registration form config", "error");
    }
  }, [d3, e3, openSnackbar]);

  useEffect(() => {
    f3({ page: "new-registration", instituteId });

    return () => {
      r3();
    };
  }, []);

  useEffect(() => {
    localStorage.clear();
  }, []);

  useEffect(() => {
    if (paramBranch) {
      setBranch(paramBranch);
    }
  }, [paramBranch]);

  const isValidBranch = useMemo(() => {
    if (!paramBranch) return true;
    if (!branchesList) return false;
    if (branchesList.length === 0) return false;
    return branchesList.find((branch) => branch.branchCode === paramBranch);
  }, [branchesList, paramBranch]);

  const classes = useStyles();
  const mobileFormRef = useRef(null);

  let boards_options = ["STATE", "CBSE", "ICSE"];

  if (window.location.hostname === "excellencia.onesaz.com") {
    boards_options = ["STATE", "CBSE", "ICSE", "OTHER"];
  }

  const formatTime = (seconds) => {
    const duration = moment.duration(seconds, "seconds");
    return `${duration.minutes()}:${duration
      .seconds()
      .toString()
      .padStart(2, "0")}`;
  };

  const getSuffix = (number) => {
    const suffixes = ["th", "st", "nd", "rd"];
    const value = number % 100;
    return suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0];
  };

  const renderOptionText = (number) => {
    return (
      <>
        {" "}
        Into&nbsp;&nbsp;
        {number}
        <sup>{getSuffix(Number(number))}</sup> Class
      </>
    );
  };

  useEffect(() => {
    if (myBranchesList && myBranchesList.length === 1) {
      setBranch(myBranchesList[0].branchCode);
    }
  }, [myBranchesList]);

  // Auto-select zone when only one option is available
  useEffect(() => {
    if (
      haveZonesFilter &&
      myZonesList[schoolOrCollege] &&
      myZonesList[schoolOrCollege].length === 1
    ) {
      const singleZone = myZonesList[schoolOrCollege][0];
      setZone(singleZone);
    }
  }, [haveZonesFilter, schoolOrCollege, myZonesList]);

  useEffect(() => {
    if (
      branch &&
      branchesData[branch] &&
      Array.isArray(branchesData[branch].classes)
    ) {
      const gradeOptions = branchesData[branch].classes;
      if (gradeOptions.length === 1 && grade !== gradeOptions[0]) {
        setGrade(gradeOptions[0]);
      }
    }
  }, [branch, branchesData, grade]);

  useEffect(() => {
    if (grade && branch) {
      const some = branchesData[branch].batches;
      if (some && some[grade] && some[grade].length === 1) {
        setBatch(some[grade][0]);
      }
    }
  }, [grade, branch]);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Get current step index
  const getCurrentStepIndex = () => {
    return Object.values(steps).indexOf(step);
  };

  // Navigate to next step
  const handleNextStep = () => {
    const currentIndex = getCurrentStepIndex();
    if (currentIndex < Object.values(steps).length - 1) {
      const nextStep = Object.values(steps)[currentIndex + 1];
      setStep(nextStep);
    }
  };

  // Navigate to previous step
  const handlePreviousStep = () => {
    const currentIndex = getCurrentStepIndex();
    if (currentIndex > 0) {
      const prevStep = Object.values(steps)[currentIndex - 1];
      setStep(prevStep);
    } else if (isLargeScreen) {
      setDialogOpen(false);
    }
  };

  // Open dialog for desktop when moving to next steps
  useEffect(() => {
    if (isLargeScreen && step !== steps.CAMPUS_SELECTION && !dialogOpen) {
      setDialogOpen(true);
    }
  }, [isLargeScreen, step, dialogOpen]);

  useEffect(() => {
    if (!isLargeScreen) {
      if (mobileFormRef.current) {
        mobileFormRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }, [step, isLargeScreen]);

  const handleConfirmDialogOpen = () => {
    if (branch === "PYP" || branch === "TNK") {
      openSnackbar(
        "Registrations for AVAGAHANA SADASSU (2025-2026) are closed",
        "warning",
        false
      );
      return;
    }
    // Keep dialog open for desktop
    if (isLargeScreen) {
      setDialogOpen(true);
    }
    setStep(steps.CONFIRMATION);
  };

  const handleConfirmDialogClose = () => {
    setStep(steps.SIMPLE_DETAILS);
    if (isLargeScreen) {
      setDialogOpen(true);
    }
  };

  const handleConfirmDialogConfirm = () => {
    setStep(steps.OTP_VERIFICATION);
    // Keep dialog open for desktop
    if (isLargeScreen) {
      setDialogOpen(true);
    }
    f1({
      phoneNumber,
      branchName: branchesData[branch] ? branchesData[branch].branchName : "",
      branch,
      academicYear,
    });
  };

  // Handle campus selection data
  const handleCampusSelectionNext = (data) => {
    if (haveZonesFilter && !data.zone) {
      openSnackbar("Please select a zone", "error");
      return;
    }
    if (!data.branch) {
      openSnackbar("Please select a campus", "error");
      return;
    }
    setBranch(data.branch || null);
    setZone(data.zone || null);
    setSchoolOrCollege(data.schoolOrCollege || null);
    handleNextStep();
  };

  // Handle field changes from campus selection
  const handleCampusFieldChange = (field, value) => {
    if (field === "schoolOrCollege") {
      setSchoolOrCollege(value);
      setZone(null);
      setBranch(null);
      setGrade(null);
      setBatch(null);
    } else if (field === "zone") {
      setZone(value);
      setBranch(null);
      setGrade(null);
      setBatch(null);
    } else if (field === "branch") {
      setBranch(value);
      setGrade(null);
      setBatch(null);
    }
  };

  // Render Campus Selection Step
  const renderCampusSelectionStep = () => {
    return (
      <CampusSelectionStage
        initialValues={{
          branch,
          zone,
          schoolOrCollege,
        }}
        haveSchoolOrCollegeFilter={haveSchoolOrCollegeFilter}
        haveZonesFilter={haveZonesFilter}
        schoolOrCollege={schoolOrCollege}
        myZonesList={myZonesList}
        myBranchesList={myBranchesList}
        branchesList={branchesList}
        branchesData={branchesData}
        paramBranch={paramBranch}
        onNext={handleCampusSelectionNext}
        onFieldChange={handleCampusFieldChange}
      />
    );
  };

  // Handle simple details data
  const handleSimpleDetailsNext = (data) => {
    setGrade(data.grade || null);
    setBatch(data.batch || null);
    setBoards(data.boards || null);
    setPhoneNumber(data.phoneNumber || "");
    handleConfirmDialogOpen();
  };

  // Handle field changes from simple details
  const handleSimpleDetailsFieldChange = (field, value) => {
    if (field === "grade") {
      setGrade(value);
      setBatch(null);
    } else if (field === "batch") {
      setBatch(value);
    } else if (field === "boards") {
      setBoards(value);
    }
  };

  // Render Simple Details Step
  const renderSimpleDetailsStep = () => {
    return (
      <SimpleDetailsStage
        initialValues={{
          grade,
          batch,
          boards,
          phoneNumber,
        }}
        schoolOrCollege={schoolOrCollege}
        school_classes={school_classes}
        college_classes={college_classes}
        branchesData={branchesData}
        boards_options={boards_options}
        branch={branch}
        onNext={handleSimpleDetailsNext}
        onBack={handlePreviousStep}
        onFieldChange={handleSimpleDetailsFieldChange}
        openSnackbar={openSnackbar}
        renderOptionText={renderOptionText}
      />
    );
  };

  // Render Confirmation Step
  const renderConfirmationStep = () => {
    return (
      <ConfirmationStage
        haveSchoolOrCollegeFilter={haveSchoolOrCollegeFilter}
        haveZonesFilter={haveZonesFilter}
        schoolOrCollege={schoolOrCollege}
        zone={zone}
        branch={branch}
        branchesData={branchesData}
        grade={grade}
        boards={boards}
        phoneNumber={phoneNumber}
        handleConfirmClick={handleConfirmDialogConfirm}
        handleCancelClick={handleConfirmDialogClose}
        renderOptionText={renderOptionText}
      />
    );
  };

  // Render OTP Verification Step
  const renderOtpVerificationStep = () => {
    return (
      <OtpVerificationStage
        phoneNumber={phoneNumber}
        l2={l2}
        isResendDisabled={isResendDisabled}
        resendTimer={resendTimer}
        formatTime={formatTime}
        resendOtp={resendOtp}
        submitOtp={submitOtp}
        onBack={() => {
          setStep(steps.CONFIRMATION);
          clear();
        }}
      />
    );
  };

  const renderForm = () => {
    if (step === steps.OTP_VERIFICATION || otpSent) {
      return renderOtpVerificationStep();
    }

    if (step === steps.CONFIRMATION) {
      return renderConfirmationStep();
    }

    return (
      <StyledBox padding={4} className={classes.relative}>
        <Guidelines />
        <Box textAlign="center">
          {stuff && (
            <img src={stuff[imageKey]} alt="Institute Logo" height="120" />
          )}
        </Box>
        {step === steps.CAMPUS_SELECTION && (
          <>
            {renderCampusSelectionStep()}
            <Box textAlign="center" mt={2}>
              <Typography variant="body2">
                Already registered up?{" "}
                <Link to="/reg-login" style={{ textDecoration: "none" }}>
                  Login
                </Link>
              </Typography>
            </Box>
          </>
        )}
        {step === steps.SIMPLE_DETAILS && renderSimpleDetailsStep()}
      </StyledBox>
    );
  };

  if (paramBranch && (paramBranch === "PYP" || paramBranch === "TNK")) {
    return (
      <Box
        textAlign="center"
        mt={2}
        style={{
          maxWidth: 400,
          margin: "auto",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" color="error">
          <b>Registrations for AVAGAHANA SADASSU (2025-2026) are closed</b>
        </Typography>
      </Box>
    );
  }

  if (l1 || l2 || l3) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (paramBranch && branchesList.length > 0 && !isValidBranch) {
    return (
      <Box
        textAlign="center"
        mt={2}
        style={{
          maxWidth: 400,
          margin: "auto",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" color="error">
          <b>Please check the URL</b>
        </Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
        flexDirection="column"
      >
        {isLargeScreen ? (
          <Box display="flex" flexDirection="column" width="100%">
            {/* Top Section - Hero */}
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              minHeight="70vh"
              maxHeight="70vh"
              className={classes.titleContainer}
              style={{ overflow: "visible" }}
            >
              {stuff && brandMaintainedInstitutes.includes(instituteId) ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="70vh"
                  maxHeight="70vh"
                  width="100%"
                  style={{ padding: "32px 24px", overflow: "visible" }}
                >
                  <HeroCarousel
                    images={brandImagesOfInstitute.filter(Boolean)}
                    interval={4000}
                    autoPlay={true}
                  />
                </Box>
              ) : (
                <Box className={classes.heroContent}>
                  {stuff && stuff[imageKey] && (
                    <Box mb={4}>
                      <img
                        src={stuff[imageKey]}
                        alt="Institute Logo"
                        className={classes.heroLogo}
                      />
                    </Box>
                  )}
                  <Typography className={classes.heroTitle}>
                    {stuff ? stuff.title : "Welcome"}
                  </Typography>
                  <Typography className={classes.heroSubtitle}>
                    {title || "Student Registration"}
                    {academicYear ? ` (${academicYear})` : ""}
                  </Typography>

                  {countDown && (
                    <Box mt={4}>
                      <BoxCountdown countDown={countDown} />
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* Bottom Section - Get Started */}
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              minHeight="30vh"
              maxHeight="30vh"
              bgcolor={colors.background.default}
              style={{ padding: `${24}px ${24}px` }}
              position="relative"
            >
              {step === steps.CAMPUS_SELECTION && !dialogOpen ? (
                <Box className={classes.rightColumnContent}>
                  <Typography className={classes.rightColumnTitle}>
                    Ready to Register?
                  </Typography>

                  <Button
                    variant="contained"
                    className={classes.startButton}
                    onClick={() => setDialogOpen(true)}
                    size="large"
                    fullWidth
                  >
                    Start Registration
                  </Button>
                  <Typography variant="body2" color="textSecondary">
                    Already have an account?{" "}
                    <Link
                      to="/reg-login"
                      style={{
                        color: colors.gradients.tirumala.main,
                        textDecoration: "none",
                        fontWeight: 600,
                      }}
                    >
                      Login here
                    </Link>
                  </Typography>
                </Box>
              ) : (
                <Dialog
                  open={dialogOpen || step !== steps.CAMPUS_SELECTION}
                  onClose={() => {
                    if (step === steps.OTP_VERIFICATION) {
                      return;
                    }
                    clearAllData();
                  }}
                  maxWidth="md"
                  PaperProps={{
                    style: {
                      maxHeight: "90vh",
                      overflow: "auto",
                    },
                  }}
                >
                  <DialogTitle>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="h6">Registration</Typography>
                      {step !== steps.OTP_VERIFICATION && (
                        <IconButton
                          aria-label="close"
                          onClick={clearAllData}
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      )}
                    </Box>
                  </DialogTitle>
                  <DialogContent>{renderForm()}</DialogContent>
                </Dialog>
              )}
            </Box>
          </Box>
        ) : (
          <Box
            width="100%"
            minHeight="100vh"
            bgcolor={colors.background.default}
            style={{ padding: `${16}px ${8}px` }}
          >
            {step !== steps.OTP_VERIFICATION && step !== steps.CONFIRMATION && (
              <Box
                textAlign="center"
                mb={3}
                pb={2}
                style={{
                  background: `linear-gradient(135deg, ${
                    colors.gradients.tirumala.main
                  } 0%, ${colors.gradients.tirumala.state} 100%)`,
                  borderRadius: 16,
                  padding: "24px 16px",
                  marginBottom: 24,
                }}
              >
                {stuff && stuff[imageKey] && (
                  <Box mb={2}>
                    <img
                      src={stuff[imageKey]}
                      alt="Institute Logo"
                      style={{ maxHeight: "80px", maxWidth: "200px" }}
                    />
                  </Box>
                )}
                <Typography
                  variant="h4"
                  style={{
                    color: colors.white.main,
                    fontWeight: 700,
                    marginBottom: 8,
                  }}
                >
                  {stuff ? stuff.title : "Welcome"}
                </Typography>
                <Typography
                  variant="h6"
                  style={{
                    color: colors.white.main,
                    opacity: 0.95,
                    fontWeight: 300,
                  }}
                >
                  {title}
                  {academicYear ? ` (${academicYear})` : ""}
                </Typography>
                {countDown && (
                  <Box mt={2}>
                    <BoxCountdown countDown={countDown} />
                  </Box>
                )}
              </Box>
            )}
            <Box ref={mobileFormRef}>{renderForm()}</Box>
          </Box>
        )}
        <RegFooter />
      </Box>
    </ThemeProvider>
  );
};

export default NewRegistration;
